import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";

const LOGO_URL = "/splash/splash-logo.webp";
// Splash background — the illustrated Ghanaian trades collage used
// on the landing hero. Keeps splash + first-paint hero visually
// consistent so returning users never see a jarring black frame.
const SPLASH_BG_URL = "/splash/splash-bg.webp";

// Match the phone status bar to the dark splash, then restore the app teal.
const setNativeBar = (dark) => {
  if (!Capacitor.isNativePlatform()) return;
  StatusBar.setBackgroundColor({ color: dark ? "#0b0b0b" : "#4F988E" }).catch(() => {});
  StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
};

const setThemeColor = (dark) => {
  const tc = document.querySelector('meta[name="theme-color"]');
  if (!tc) return;
  if (!tc.dataset.appColor) tc.dataset.appColor = tc.content;
  tc.content = dark ? "#0b0b0b" : tc.dataset.appColor;
};

const removeBoot = () => {
  document.getElementById("yorm-boot-splash")?.remove();
  document.documentElement.classList.remove("yorm-boot");
};

export default function SplashScreen({ onComplete, duration = 3000 }) {
  const [fadeOut, setFadeOut] = useState(false);

  // Hand-off from the static boot backdrop (index.html) and the native
  // launch screen — both show the same art, so the swap is invisible.
  useEffect(() => {
    setNativeBar(true);
    setThemeColor(true);
    const t = setTimeout(removeBoot, 1500);
    return () => { clearTimeout(t); setNativeBar(false); setThemeColor(false); };
  }, []);

  useEffect(() => {
    // Kick off a longer, smoother fade well before we tear the splash
    // out of the tree — 900 ms of eased opacity feels cinematic instead
    // of the previous 500 ms flash that looked janky on mid-range Androids.
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, duration - 900);

    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, duration);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [duration, onComplete]);

  return (
    <div
      // Full-page coverage: `inset-0` pins all four edges, explicit
      // 100vw/100dvh (with vh fallback) guarantees iOS Safari's dynamic
      // viewport is fully covered including the address-bar & home-
      // indicator safe areas. z-index sits above every other overlay
      // (dialogs, toasts, headers, live-fleet map controls).
      //
      // `will-change: opacity` + a GPU-friendly `translateZ(0)` promote
      // the whole splash into its own compositor layer so the fade is
      // a single texture crossfade instead of a per-frame repaint (the
      // root cause of the "janky fade" reports on older Androids).
      className={`fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
      style={{
        width: "100vw",
        height: "100vh",
        minHeight: "100dvh",
        top: 0, right: 0, bottom: 0, left: 0,
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
        // Fallback solid tone matches the artwork so any brief moment
        // before the PNG paints doesn't flash pure black.
        backgroundColor: "#0b0b0b",
        transition: "opacity 900ms cubic-bezier(0.4, 0, 0.2, 1)",
        willChange: "opacity",
        transform: "translateZ(0)",
      }}
      data-testid="splash-screen"
    >
      {/* Illustrated background — absolutely positioned so the logo
          layer above it stays perfectly centred. The brightness/contrast
          filter was removed because CSS `filter` forces a per-frame
          composite during the crossfade on Safari + older Chrome, which
          was the biggest contributor to the janky-fade report. A softer
          scrim on top delivers the same visual balance without the cost. */}
      <img
        src={SPLASH_BG_URL}
        alt=""
        aria-hidden="true"
        onLoad={removeBoot}
        decoding="sync"
        fetchPriority="high"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      {/* Soft dark scrim so the coloured Yorm logo pops against the
          busy illustrated backdrop without hiding the icons. */}
      <div className="absolute inset-0 bg-black/40 pointer-events-none" />

      <div className="relative flex flex-col items-center">
        {/* Logo with slow motion scale and glow animation */}
        <div className="relative animate-slow-scale">
          <img
            src={LOGO_URL}
            alt="Yorm"
            decoding="async"
            fetchPriority="high"
            className="w-40 h-40 md:w-52 md:h-52 object-contain animate-slow-glow"
          />
        </div>
      </div>
    </div>
  );
}
